/*

? Which kind a chat attachment is: a photo, a document or a voice note.

Kept free of database code so the web chat (a client component) can import it as well as
the server (lib/chat/resolveChatAttachment.ts). The Android app keeps the same lists in
core/utils/chat/ChatAttachments.kt; change both together.

Voice notes (15 Oct 2026) are recorded in the app as AAC in an MP4 container
(`audio/mp4`); the other audio types are what other recorders produce (iOS, browsers), so
a voice note from any of them is accepted and played.

*/

/* Mime types a chat attachment is sent and shown as a document (a file tile with a link). */
export const CHAT_FILE_MIME_TYPES: Array<string> = ["application/pdf"];

/* Mime types a chat attachment is sent and played as a voice note. */
export const CHAT_AUDIO_MIME_TYPES: Array<string> = [
	"audio/mp4",
	"audio/m4a",
	"audio/x-m4a",
	"audio/aac",
	"audio/mpeg",
	"audio/ogg",
	"audio/webm",
];

/* The largest voice note send-message accepts: 10 MB (the app caps a recording at 10 minutes, about 5 MB). */
export const CHAT_AUDIO_MAX_BYTES: number = 10 * 1024 * 1024;

/* True when a stored or resolved chat attachment is a document, not a photo. */
export function isChatDocument(attachment: { mime_type?: string } | null | undefined): boolean {
	return CHAT_FILE_MIME_TYPES.includes(String(attachment?.mime_type || ""));
}

/* True when a stored or resolved chat attachment is a voice note. */
export function isChatAudio(attachment: { mime_type?: string } | null | undefined): boolean {
	return CHAT_AUDIO_MIME_TYPES.includes(String(attachment?.mime_type || ""));
}

/* True when a stored or resolved chat attachment is a photo (neither a document nor a voice note). */
export function isChatPhoto(attachment: { mime_type?: string } | null | undefined): boolean {
	return !isChatDocument(attachment) && !isChatAudio(attachment);
}

/*
 * What a message that is only attachments previews as in the chat list: "Voice note",
 * "Photo" or "Document" (plural for several of one kind), or "" when there are none.
 */
export function attachmentPreviewLabel(attachments: Array<{ mime_type?: string }> | null | undefined): string {
	const list = attachments || [];
	if (list.length === 0) return "";
	if (list.every(isChatAudio)) return "Voice note";
	if (list.every(isChatDocument)) return list.length > 1 ? "Documents" : "Document";
	if (list.every(isChatPhoto)) return list.length > 1 ? "Photos" : "Photo";
	return "Attachments";
}
